import { type ChangeEvent, useCallback, useMemo, useState } from 'react';

/**
 * Normalizes a string by removing diacritics (accents).
 * e.g., "café" -> "cafe", "naïve" -> "naive"
 */
function normalize(str: string): string {
  return str.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Checks if all search terms are found in the target text.
 * - Case insensitive
 * - Handles diacritics (é matches e)
 * - Multi-term search (space-separated terms must all match)
 */
function matchesSearch(searchString: string, text: string): boolean {
  if (!searchString) return true;

  const terms = searchString.toLowerCase().split(/\s+/);
  const normalizedTarget = normalize(text.toLowerCase());

  return terms.every((term) => normalizedTarget.includes(normalize(term)));
}

interface UseFilteredListOptions<T> {
  /** Function to extract searchable text from an item */
  getSearchText: (item: T) => string;
  /** Initial search string. Default: '' */
  initialSearch?: string;
}

interface UseFilteredListResult<T> {
  /** The filtered list of items */
  filtered: T[];
  /** Current search string */
  search: string;
  /** Set the search string directly */
  setSearch: (value: string) => void;
  /** Handler for input onChange events */
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Whether a search is active (non-empty search string) */
  isSearching: boolean;
}

/**
 * A hook for client-side filtering of lists with search. Ported verbatim from
 * the Next app's hooks/use-filtered-list.ts.
 */
export function useFilteredList<T>(
  items: T[],
  options: UseFilteredListOptions<T>,
): UseFilteredListResult<T> {
  const { getSearchText, initialSearch = '' } = options;
  const [search, setSearch] = useState(initialSearch);

  const filtered = useMemo(() => {
    const trimmed = search.trim();
    if (!trimmed) return items;

    return items.filter((item) => matchesSearch(trimmed, getSearchText(item)));
  }, [items, search, getSearchText]);

  const onSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  return {
    filtered,
    search,
    setSearch,
    onSearchChange,
    isSearching: search.trim().length > 0,
  };
}
